// The rewards file — the .json a board's reward table travels in, read and
// described on the client.
//
// Two screens take such a file and they must turn away the same files for the
// same reasons: the Rewards tab, which hands it to the server for a plan, and
// the create form, which never talks to the server at all and turns the rows
// straight into draft rewards. Reading it lives here so those two can't drift
// the way the payload rules once did (see rewardPayload.ts).
//
// Mirrors internal/admin/snapshot/rewards.go. Nothing here validates a reward
// against a board — the range rules belong to whoever knows the board.

import {
  isTierThresholdReward,
  type RankBasis,
  type RewardsFile,
  type RewardsFileEntry,
} from "./types";

/**
 * The one accepted value of the envelope's `schema` field. Mirrors
 * snapshot.RewardsSchema; both sides exact-match it and reject anything else
 * instead of attempting a partial parse.
 */
export const REWARDS_FILE_SCHEMA = "social-hub/leaderboard-rewards@1";

/**
 * The biggest file either import screen will read, and what maxRewardsBodyBytes
 * accepts on the other end. Both screens promise ".json · up to 1 MB" in their
 * dropzone, so the number and the sentence that turns a file away for missing
 * it live here rather than once per screen.
 */
export const MAX_REWARDS_FILE_BYTES = 1 << 20;

/** Why a file over that size is turned away, in one voice on both screens. */
export const REWARDS_FILE_TOO_BIG =
  "This file is larger than 1 MB. A reward table never is — check you picked the right one.";

/**
 * Read a picked file as text. The same three lines TransferPage keeps for the
 * snapshot import; kept here so the two rewards screens share one reader
 * instead of a copy each.
 */
export function readRewardsFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsText(file);
  });
}

/**
 * The name an exported file gets: `<slug>-rewards-<YYYY-MM-DD>.json`, where the
 * slug is the board's ext key lowercased with every run of non-alphanumerics
 * collapsed to a single `-`. Kept in lockstep with `rewardsFilename` in
 * internal/admin/api/handler/rewards.go, which builds the real
 * Content-Disposition name the same way — this copy is what the browser saves
 * under if a response ever arrives without that header.
 *
 * The date is read in local time: it is the date on the operator's own
 * calendar that they will look for in their downloads folder.
 */
export function rewardsFilename(extKey: string, date: Date = new Date()): string {
  // An ext key is ASCII by validation today, but the fallback costs one `||`
  // and keeps this identical to the Go side, which cannot assume that.
  const slug =
    extKey.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") ||
    "leaderboard";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${slug}-rewards-${yyyy}-${mm}-${dd}.json`;
}

/** Outcome of reading a file the operator picked. */
export type ParsedRewardsFile =
  | { ok: true; file: RewardsFile }
  | { ok: false; problems: string[] };

/**
 * One line of the problem list, in the shape the server writes into
 * `details.problems` — `reward 5 «Top 10» — <reason>`. Identical on both sides
 * so a screen can pour client-found and server-found problems into the same
 * list without them reading as two different voices.
 */
function problemLine(index: number, name: unknown, reason: string): string {
  const label =
    typeof name === "string" && name.trim() ? name.trim() : "(unnamed)";
  return `reward ${index + 1} «${label}» — ${reason}`;
}

/**
 * Read the text of a picked file. Only what the file itself can settle is
 * checked here: that it is JSON, that it is this schema, and that every row
 * carries the two fields nothing downstream can do without. Whether a row is
 * legal on a particular board is a different question, answered by the server
 * on the Rewards tab and by the form's own validators on the create screen.
 *
 * A structural fault returns alone — once the top level is wrong there is
 * nothing true left to say about the rows. Row faults are collected in full,
 * so one pass through the list tells the operator everything to fix.
 */
export function parseRewardsFile(text: string): ParsedRewardsFile {
  let root: unknown;
  try {
    root = JSON.parse(text);
  } catch {
    return {
      ok: false,
      problems: ["This file isn't valid JSON — pick the .json file an export produced."],
    };
  }

  // typeof null === "object", and an array top level is a rewards list someone
  // saved without its envelope.
  if (root === null || typeof root !== "object" || Array.isArray(root)) {
    return {
      ok: false,
      problems: ["This file isn't a rewards export — its top level is not a JSON object."],
    };
  }

  const bag = root as Record<string, unknown>;

  if (bag.schema !== REWARDS_FILE_SCHEMA) {
    const got = typeof bag.schema === "string" ? `"${bag.schema}"` : "nothing";
    return {
      ok: false,
      problems: [
        `This file isn't a rewards export: its schema is ${got}, expected "${REWARDS_FILE_SCHEMA}".`,
      ],
    };
  }

  if (!Array.isArray(bag.rewards)) {
    return {
      ok: false,
      problems: ['This file has no "rewards" list, so there is nothing to import.'],
    };
  }

  const problems: string[] = [];
  const rewards: RewardsFileEntry[] = [];
  bag.rewards.forEach((row, i) => {
    if (row === null || typeof row !== "object" || Array.isArray(row)) {
      problems.push(problemLine(i, null, "this entry is not a JSON object"));
      return;
    }
    const entry = row as Record<string, unknown>;
    if (typeof entry.name !== "string") {
      problems.push(problemLine(i, entry.name, "name must be text"));
    }
    if (typeof entry.position !== "number" || !Number.isFinite(entry.position)) {
      problems.push(problemLine(i, entry.name, "position must be a number"));
    }
    // Every other field is left exactly as written: the range fields are the
    // board's business, and the payload is the game's.
    rewards.push(row as RewardsFileEntry);
  });
  if (problems.length > 0) return { ok: false, problems };

  // `source` and `exported_at` are informational, so a file missing them still
  // imports — they are filled in here rather than at every place that prints
  // them, which would otherwise each have to guard against a half-written file.
  const source = (
    bag.source && typeof bag.source === "object" ? bag.source : {}
  ) as Record<string, unknown>;
  const str = (v: unknown): string => (typeof v === "string" ? v : "");

  return {
    ok: true,
    file: {
      schema: REWARDS_FILE_SCHEMA,
      exported_at: str(bag.exported_at),
      source: {
        host: str(source.host),
        project_name: str(source.project_name),
        board_ext_key: str(source.board_ext_key),
        board_name: str(source.board_name),
      },
      rewards,
    },
  };
}

/** Which of the three shapes a file row describes. */
export type RewardsFileEntryKind = "rank_range" | "percentile" | "tier_threshold";

/**
 * Same derivation the table uses on a saved reward — no file row carries a
 * kind field either, the populated range is the whole definition.
 */
export function fileRewardKind(e: RewardsFileEntry): RewardsFileEntryKind {
  // `?? undefined` because a hand-edited file may spell an absent field
  // `null`, while a Reward from the API only ever omits it.
  if (
    isTierThresholdReward({
      rank_from: e.rank_from ?? undefined,
      percentile_from: e.percentile_from ?? undefined,
      tier_key: e.tier_key ?? undefined,
    })
  ) {
    return "tier_threshold";
  }
  if (e.percentile_from !== undefined && e.percentile_from !== null) {
    return "percentile";
  }
  return "rank_range";
}

/**
 * What a file row's rank counts. Absent — and anything that is not the one
 * other value — reads as `entity`, the same substitution the server makes for
 * a body that names no basis; a hand-edited file is free to write nonsense
 * here, and reading it as the ordinary table is what a missing field means
 * anyway.
 *
 * It matters wherever two rows share a range: on a member_contribution board a
 * `1 – 1` in the team table and a `1 – 1` in the contributor table are two
 * different rewards, so nothing may show them as one row or fold them into
 * one.
 */
export function fileRewardBasis(e: RewardsFileEntry): RankBasis {
  return e.rank_basis === "guild_member" ? "guild_member" : "entity";
}

/** The Range cell of a file row — worded exactly as the Rewards tab words it. */
export function fileRewardRangeText(e: RewardsFileEntry): string {
  const kind = fileRewardKind(e);
  // A threshold reward has no range at all; the Tier column carries it.
  if (kind === "tier_threshold") return "—";
  if (kind === "percentile") {
    return `${e.percentile_from} – ${e.percentile_to}%`;
  }
  return `${e.rank_from} – ${e.rank_to}`;
}

/**
 * Day-first, as the mockup writes it ("exported 9 Aug 2026") — the rest of the
 * admin uses en-US month-first, but this line reads as prose rather than as a
 * column of dates.
 */
const EXPORTED_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

/**
 * The pieces of the file bar's meta line, apart rather than joined because the
 * board name is set in bold there. `exported` is null when the file carries no
 * readable timestamp — provenance is informational, so a file without one is
 * described without it rather than refused.
 */
export interface RewardsFileMeta {
  count: string;
  boardName: string;
  projectName: string;
  exported: string | null;
}

export function rewardsFileMeta(file: RewardsFile): RewardsFileMeta {
  const n = file.rewards.length;
  const exportedAt = file.exported_at ? new Date(file.exported_at) : null;
  return {
    count: `${n} ${n === 1 ? "reward" : "rewards"}`,
    // The ext key stands in for a missing name: it is the board's other name,
    // and an empty segment would tell the operator nothing at all.
    boardName: file.source.board_name.trim() || file.source.board_ext_key.trim(),
    projectName: file.source.project_name.trim(),
    exported:
      exportedAt && !Number.isNaN(exportedAt.getTime())
        ? `exported ${EXPORTED_FMT.format(exportedAt)}`
        : null,
  };
}
